import React, { FC, Suspense } from 'react';
import ContentLoader from 'react-content-loader';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import { TOOL_CATEGORY_LABELS, TOOL_CATEGORY_ORDER, getToolInfo } from '../common/constants';
import {
    ItemDot,
    ItemMeta,
    OverviewBody,
    OverviewCard,
    OverviewCardBadge,
    OverviewCardEmpty,
    OverviewCardHeader,
    OverviewCardItem,
    OverviewCardTitle,
    OverviewCategoryTitle,
    OverviewGrid,
    StructureSummaryCard,
    StructureSummaryGrid,
    StructureSummaryLabel,
    StructureSummaryValue
} from '../common/styled';
import type { ProductToolData, SidePanelProduct, ToolDefinition } from '../common/types';
import { useConfiguratorData } from '../common/useConfiguratorData';

export interface FullScreenOverviewProps {
    product: SidePanelProduct;
    productStructureSummary: { totalVariants: number; maxSides: number; maxPrintAreas: number };
    availableTools: ToolDefinition[];
    toolData: ProductToolData;
}

const ComposerOverview: FC<{ product: SidePanelProduct }> = ({ product }) => {
    const configuratorData = useConfiguratorData(product.id);
    return (
        <>
            <OverviewCategoryTitle>{T._('Scene', 'Admin')}</OverviewCategoryTitle>
            <OverviewCard>
                <OverviewCardHeader>
                    <OverviewCardTitle>{T._('Meshes', 'Admin')}</OverviewCardTitle>
                    <OverviewCardBadge $active={configuratorData.sceneMeshes.length > 0}>{configuratorData.sceneMeshes.length}</OverviewCardBadge>
                </OverviewCardHeader>
                {configuratorData.sceneMeshes.map((mesh, i) => (
                    <OverviewCardItem key={i}>
                        <ItemDot $color='#3b82f6' />
                        <span>
                            {mesh.meshID.substring(0, 8)} ({mesh.sides.length} {mesh.sides.length === 1 ? T._('side', 'Admin') : T._('sides', 'Admin')}
                            {mesh.materials.length > 0 &&
                                `, ${mesh.materials.length} ${mesh.materials.length === 1 ? T._('material', 'Admin') : T._('materials', 'Admin')}`}
                            )
                        </span>
                    </OverviewCardItem>
                ))}
            </OverviewCard>
            <OverviewCategoryTitle>{T._('Composer', 'Admin')}</OverviewCategoryTitle>
            <OverviewCard>
                <OverviewCardHeader>
                    <OverviewCardTitle>{T._('Attributes', 'Admin')}</OverviewCardTitle>
                    <OverviewCardBadge $active={configuratorData.attributes.length > 0}>{configuratorData.attributes.length}</OverviewCardBadge>
                </OverviewCardHeader>
                {configuratorData.attributes.map((attr, i) => (
                    <OverviewCardItem key={i}>
                        <ItemDot $color='#3b82f6' />
                        <span>
                            {attr.name} ({attr.options.length} {T._('options', 'Admin')})
                        </span>
                    </OverviewCardItem>
                ))}
            </OverviewCard>
        </>
    );
};

export const FullScreenOverview: FC<FullScreenOverviewProps> = ({ product, productStructureSummary, availableTools, toolData }) => {
    return (
        <OverviewBody>
            <OverviewGrid>
                {product.zakekeEnvironment === 'COMPOSER' ? (
                    <Suspense fallback={null}>
                        <ComposerOverview product={product} />
                    </Suspense>
                ) : (
                    <>
                        <OverviewCategoryTitle>{T._('Product Structure', 'Admin')}</OverviewCategoryTitle>
                        <OverviewCard>
                            <OverviewCardHeader>
                                <OverviewCardTitle>{T._('Product structure', 'Admin')}</OverviewCardTitle>
                            </OverviewCardHeader>
                            <StructureSummaryGrid>
                                <StructureSummaryCard>
                                    {productStructureSummary.totalVariants === 0 ? (
                                        <StructureSummaryLabel>{T._('No variants', 'Admin')}</StructureSummaryLabel>
                                    ) : (
                                        <>
                                            <StructureSummaryValue>{productStructureSummary.totalVariants}</StructureSummaryValue>
                                            <StructureSummaryLabel>
                                                {productStructureSummary.totalVariants === 1 ? T._('Variant', 'Admin') : T._('Variants', 'Admin')}
                                            </StructureSummaryLabel>
                                        </>
                                    )}
                                </StructureSummaryCard>
                                <StructureSummaryCard>
                                    <StructureSummaryValue>{productStructureSummary.maxSides}</StructureSummaryValue>
                                    <StructureSummaryLabel>
                                        {productStructureSummary.maxSides === 1 ? T._('Side', 'Admin') : T._('Sides', 'Admin')}
                                    </StructureSummaryLabel>
                                </StructureSummaryCard>
                                <StructureSummaryCard>
                                    <StructureSummaryValue>{productStructureSummary.maxPrintAreas}</StructureSummaryValue>
                                    <StructureSummaryLabel>
                                        {productStructureSummary.maxPrintAreas === 1 ? T._('Print Area', 'Admin') : T._('Print Areas', 'Admin')}
                                    </StructureSummaryLabel>
                                </StructureSummaryCard>
                            </StructureSummaryGrid>
                        </OverviewCard>
                    </>
                )}
                {TOOL_CATEGORY_ORDER.map((category) => {
                    const categoryTools = availableTools.filter((t) => t.category === category);
                    if (categoryTools.length === 0) return null;
                    return (
                        <React.Fragment key={category}>
                            <OverviewCategoryTitle>{TOOL_CATEGORY_LABELS[category]}</OverviewCategoryTitle>
                            {categoryTools.map((tool) => {
                                const info = getToolInfo(product, tool, toolData);
                                return (
                                    <OverviewCard key={tool.key}>
                                        <OverviewCardHeader>
                                            <OverviewCardTitle>{tool.name}</OverviewCardTitle>
                                            <OverviewCardBadge $active={info.count > 0}>{info.count > 0 ? info.count : '--'}</OverviewCardBadge>
                                        </OverviewCardHeader>
                                        {!toolData.loaded ? (
                                            <OverviewCardEmpty>
                                                <ContentLoader
                                                    speed={2}
                                                    width='100%'
                                                    height='14'
                                                    backgroundColor={theme.skeleton.backgroundColor}
                                                    foregroundColor={theme.skeleton.foregroundColor}
                                                >
                                                    <rect x='0' y='0' rx='4' ry='4' width='80%' height='14' />
                                                </ContentLoader>
                                            </OverviewCardEmpty>
                                        ) : info.items.length > 0 ? (
                                            info.items.slice(0, 10).map((item) => (
                                                <OverviewCardItem key={item.id}>
                                                    <ItemDot $color='#3b82f6' />
                                                    <span>{item.name}</span>
                                                </OverviewCardItem>
                                            ))
                                        ) : info.status ? (
                                            <OverviewCardItem>
                                                <ItemDot $color='#10b981' />
                                                <span>{info.status}</span>
                                            </OverviewCardItem>
                                        ) : (
                                            <OverviewCardEmpty>{T._('Not configured', 'Admin')}</OverviewCardEmpty>
                                        )}
                                        {info.items.length > 10 && (
                                            <OverviewCardItem>
                                                <ItemMeta>
                                                    +{info.items.length - 10} {T._('more', 'Admin')}
                                                </ItemMeta>
                                            </OverviewCardItem>
                                        )}
                                    </OverviewCard>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </OverviewGrid>
        </OverviewBody>
    );
};
